import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Select } from "antd";
import { Column } from "@ant-design/plots";
import { Layout } from "../../../ui";
import { getDashboardSummary, DashboardSummaryData } from "../api";
import styles from "../styles/dashboard.module.css";

const { Option } = Select;

interface ChartDataPoint {
  label: string;
  value: number;
  date?: string;
}

type TimePeriod = 'daily' | 'weekly' | 'monthly' | 'yearly';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState < DashboardSummaryData | null > (null);
  const [loading, setLoading] = useState < boolean > (true);
  const [error, setError] = useState < string | null > (null);
  const [selectedPeriod, setSelectedPeriod] = useState < TimePeriod > ('daily');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await getDashboardSummary();
        setDashboardData(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch dashboard data");
        console.error("Dashboard API error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getTotalCounts = () => {
    if (!dashboardData) return { daily: 0, weekly: 0, monthly: 0, yearly: 0 };

    return {
      daily: dashboardData.daily[0]?.count || 0,
      weekly: dashboardData.weekly[0]?.count || 0,
      monthly: dashboardData.monthly[0]?.count || 0,
      yearly: dashboardData.yearly[0]?.count || 0,
      
    };
  };

  const getChartData = (): ChartDataPoint[] => {
    if (!dashboardData) return [];

    switch (selectedPeriod) {
      case 'daily':
        return dashboardData.daily.map(item => ({
          label: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          value: item.count || 0,
          date: item.date
        }));

      case 'weekly':
        return dashboardData.weekly.map(item => ({
          label: item.week,
          value: item.count || 0,
          date: item.date
        }));

      case 'monthly':
        return dashboardData.monthly.map(item => ({
          label: new Date(item.date + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'short' }),
          value: item.count || 0,
          date: item.date
        }));

      case 'yearly':
        return dashboardData.yearly.map(item => ({
          label: item.date.toString(),
          value: item?.count || 0,
          date: item.date.toString()
        }));

      default:
        return [];
    }
  };

  const getCurrentPeriodData = () => {
    if (!dashboardData) return [];

    switch (selectedPeriod) {
      case 'daily':
        return dashboardData.daily;
      case 'weekly':
        return dashboardData.weekly;
      case 'monthly':
        return dashboardData.monthly;
      case 'yearly':
        return dashboardData.yearly;
      default:
        return [];
    }
  };

  const totals = getTotalCounts();
  const chartData = getChartData();
  const currentPeriodData = getCurrentPeriodData();



  const chartConfig = {
    data: chartData,
    xField: 'label',
    yField: 'value',
    columnStyle: {
      fill: '#03FE9D',
      radius: [4, 4, 0, 0],
    },
    color: '#03FE9D',
    columnWidthRatio: 0.6,
    xAxis: {
      label: {
        style: {
          fill: '#ffffff',
          fontSize: 12,
        },
      },
      line: {
        style: {
          stroke: '#333',
        },
      },
      tickLine: {
        style: {
          stroke: '#333',
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fill: '#ffffff',
          fontSize: 12,
        },
      },
      grid: {
        line: {
          style: {
            stroke: '#333',
            lineDash: [3, 3],
          },
        },
      },
      line: {
        style: {
          stroke: '#333',
        },
      },
    },
    tooltip: {
      customContent: (title: string, items: any[]) => {
        if (!items || items.length === 0) return '';
        const item = items[0];
        return `
          <div style="padding: 8px; background: #111213; border: 1px solid #03FE9D; border-radius: 4px;">
            <div style="color: #fff; font-weight: bold; margin-bottom: 4px;">${title}</div>
            <div style="color: #03FE9D;">Orders: ${item.value}</div>
          </div>
        `;
      },
    },
    theme: {
      backgroundColor: 'transparent',
    },
  };

  const periodOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  if (loading) {
    return (
      <Layout title="Dashboard">
        <Container fluid className={styles.loadingContainer}>
          <h3 className={styles.loadingText}>Loading dashboard data...</h3>
        </Container>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Dashboard">
        <Container fluid className={styles.loadingContainer}>
          <h3 className={styles.errorText}>{error}</h3>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard">
      <Container fluid className={styles.dashboardContainer}>
        <Row className={`g-3 ${styles.summaryCardsContainer}`}>
          <Col xl={3} lg={6} md={6} sm={6}>
            <Card className={styles.summaryCard}>
              <Card.Body className="p-0">
                <h3 className={styles.cardTitle}>ICCID Active</h3>
                <p className={styles.cardValue}>{dashboardData?.iccidStatus?.active ?? 0}</p>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} lg={6} md={6} sm={6}>
            <Card className={styles.summaryCard}>
              <Card.Body className="p-0">
                <h3 className={styles.cardTitle}>ICCID Inventory</h3>
                <p className={styles.cardValue}>{dashboardData?.iccidStatus?.inactive ?? 0}</p>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} lg={6} md={6} sm={6}>
            <Card className={styles.summaryCard}>
              <Card.Body className="p-0">
                <h3 className={styles.cardTitle}>Activation Request</h3>
                <p className={styles.cardValue}>{dashboardData?.activationRequestCount ?? 0}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-4 mb-4">
          <Col xl={6} lg={6} md={12} sm={12} xs={12}>
            <Card className={styles.detailsSection}>
              <Card.Body className="p-0">
                <div className={styles.chartHeader}>
                  <h3 className={styles.detailsTitle}>Order Statistics</h3>
                  <Select
                    value={selectedPeriod}
                    onChange={setSelectedPeriod}
                    className={styles.periodSelector}
                    size="large"
                  >
                    {periodOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className={styles.chartContainer}>
                  <Column {...chartConfig} />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xl={6} lg={6} md={12} sm={12} xs={12}>
            <Card className={styles.detailsSection}>
              <Card.Body className="p-0">
                <h3 className={styles.detailsTitle}>
                  {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Breakdown
                </h3>
                <Row className="g-4">
                  <Col xs={12}>
                    <div className={styles.breakdownSection}>
                      <div className={styles.breakdownList}>
                        {selectedPeriod === 'weekly' ? (
                          currentPeriodData.map((item: any, index: number) => (
                            <div key={index} className={styles.weeklyItem}>
                              <div className={styles.weeklyHeader}>
                                <span className={styles.breakdownLabel}>{item.week}</span>
                                <span className={styles.breakdownValue}>{item.count}</span>
                              </div>
                              <div className={styles.weeklyDate}>{item.date}</div>
                            </div>
                          ))
                        ) : (
                          currentPeriodData.map((item: any, index: number) => (
                            <div key={index} className={styles.breakdownItem}>
                              <span className={styles.breakdownLabel}>
                                {selectedPeriod === 'daily'
                                  ? new Date(item.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                                  : selectedPeriod === 'monthly'
                                    ? new Date(item.date + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
                                    : item.date
                                }
                              </span>
                              <span className={styles.breakdownValue}>{item.count}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Container>
    </Layout>
  );
};

export default Dashboard; 